/**
 * CustomTimeframeModal — manage user-defined timeframes.
 *
 * Allows adding, editing, and deleting custom timeframe intervals.
 * Built-in timeframes are shown read-only; custom ones are fully editable.
 * Changes are persisted to localStorage and registered at runtime.
 *
 * Also provides a "Backfill History" tab to configure how many days of
 * historical 1-minute bars are fetched when a symbol is opened.
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  BUILT_IN_TIMEFRAMES,
  TIMEFRAME_CONFIGS,
  registerCustomTimeframe,
  unregisterCustomTimeframe,
} from "../../types/chart.types";
import {
  CustomTimeframe,
  loadCustomTimeframes,
  loadChartSettings,
  BackfillDays,
  BACKFILL_DAYS_OPTIONS,
  loadBackfillDays,
  saveBackfillDays,
  EodDuration,
  EOD_DURATION_OPTIONS,
  loadEodDuration,
  saveEodDuration,
  CHART_BG_PRESETS,
  ChartBgPreset,
  loadChartBgId,
  saveChartBg,
} from "../../service/local-storage-handler";
import { setAlertSoundEnabled } from "../../store/alert-slice";
import { syncSaveCustomTimeframes, syncSaveChartSettings } from "../../service/sync-handler";
import "./CustomTimeframeModal.scss";

interface CustomTimeframeModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme: "light" | "dark";
  /** Called after custom timeframes are changed so the toolbar can refresh. */
  onTimeframesChanged?: () => void;
}

const UNIT_OPTIONS = [
  { value: "m", label: "Minutes", multiplier: 1 },
  { value: "H", label: "Hours", multiplier: 60 },
  { value: "W", label: "Weeks", multiplier: 10080 },
  { value: "Mo", label: "Months", multiplier: 43800 },
  { value: "Y", label: "Years", multiplier: 525600 },
];

type ActiveTab = "timeframes" | "backfill" | "background" | "style" | "alerts";

type CandleColorField = "up" | "down";

type RgbChannel = "r" | "g" | "b";

interface RgbColor {
  r: number;
  g: number;
  b: number;
}

interface HsvColor {
  h: number;
  s: number;
  v: number;
}

const BACKFILL_LABELS: Record<BackfillDays, string> = {
  30: "30 Days",
  60: "60 Days",
  90: "90 Days",
  180: "180 Days",
  360: "1 Year",
};

const EOD_DURATION_LABELS: Record<EodDuration, string> = {
  365: "1 Year",
  1825: "5 Years",
  3650: "10 Years",
  7300: "20 Years",
};

const DEFAULT_CANDLE_COLORS = {
  upColor: "#16a34a",
  downColor: "#dc2626",
};

const BULL_COLOR_PRESETS = [
  "#16a34a", "#22c55e", "#10b981", "#00c853", "#2e7d32", "#66bb6a",
];

const BEAR_COLOR_PRESETS = [
  "#dc2626", "#ef4444", "#f97316", "#d32f2f", "#b71c1c", "#ff5252",
];

const normalizeHexColor = (value: string): string | null => {
  const trimmed = value.trim();
  const short = /^#([0-9a-fA-F]{3})$/;
  const full = /^#([0-9a-fA-F]{6})$/;

  if (short.test(trimmed)) {
    const [, hex] = trimmed.match(short)!;
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toLowerCase();
  }

  if (full.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  return null;
};

const hexToRgb = (hexColor: string): RgbColor => {
  const normalized = normalizeHexColor(hexColor) ?? "#000000";
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
};

const rgbToHex = ({ r, g, b }: RgbColor): string => {
  const toHex = (channel: number) => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rgbToHsv = ({ r, g, b }: RgbColor): HsvColor => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;

  if (delta !== 0) {
    if (max === red) hue = 60 * (((green - blue) / delta) % 6);
    else if (max === green) hue = 60 * ((blue - red) / delta + 2);
    else hue = 60 * ((red - green) / delta + 4);
  }

  if (hue < 0) hue += 360;
  return {
    h: Math.round(hue),
    s: max === 0 ? 0 : Math.round((delta / max) * 100),
    v: Math.round(max * 100),
  };
};

const hsvToRgb = ({ h, s, v }: HsvColor): RgbColor => {
  const saturation = s / 100;
  const value = v / 100;
  const chroma = value * saturation;
  const sector = h / 60;
  const x = chroma * (1 - Math.abs((sector % 2) - 1));
  const match = value - chroma;
  let rgb: [number, number, number];

  if (sector < 1) rgb = [chroma, x, 0];
  else if (sector < 2) rgb = [x, chroma, 0];
  else if (sector < 3) rgb = [0, chroma, x];
  else if (sector < 4) rgb = [0, x, chroma];
  else if (sector < 5) rgb = [x, 0, chroma];
  else rgb = [chroma, 0, x];

  return {
    r: Math.round((rgb[0] + match) * 255),
    g: Math.round((rgb[1] + match) * 255),
    b: Math.round((rgb[2] + match) * 255),
  };
};

interface AdvancedColorSurfaceProps {
  color: string;
  label: string;
  onChange: (color: string) => void;
}

const AdvancedColorSurface: React.FC<AdvancedColorSurfaceProps> = ({ color, label, onChange }) => {
  const draggingRef = useRef(false);
  const hsv = rgbToHsv(hexToRgb(color));
  const hueColor = rgbToHex(hsvToRgb({ h: hsv.h, s: 100, v: 100 }));

  const updateSaturationValue = useCallback((clientX: number, clientY: number, element: HTMLDivElement) => {
    const rect = element.getBoundingClientRect();
    const saturation = Math.round(Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * 100);
    const value = Math.round((1 - Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))) * 100);
    onChange(rgbToHex(hsvToRgb({ ...hsv, s: saturation, v: value })));
  }, [hsv, onChange]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    updateSaturationValue(event.clientX, event.clientY, event.currentTarget);
  }, [updateSaturationValue]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    updateSaturationValue(event.clientX, event.clientY, event.currentTarget);
  }, [updateSaturationValue]);

  const stopDragging = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handlePaletteKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 10 : 2;
    let nextSaturation = hsv.s;
    let nextValue = hsv.v;

    if (event.key === "ArrowLeft") nextSaturation -= step;
    else if (event.key === "ArrowRight") nextSaturation += step;
    else if (event.key === "ArrowUp") nextValue += step;
    else if (event.key === "ArrowDown") nextValue -= step;
    else return;

    event.preventDefault();
    onChange(rgbToHex(hsvToRgb({
      ...hsv,
      s: Math.max(0, Math.min(100, nextSaturation)),
      v: Math.max(0, Math.min(100, nextValue)),
    })));
  }, [hsv, onChange]);

  return (
    <div className="ctf-advanced-picker">
      <div
        className="ctf-sv-palette"
        role="slider"
        tabIndex={0}
        aria-label={`${label} saturation and brightness`}
        aria-valuetext={`Saturation ${hsv.s}%, brightness ${hsv.v}%`}
        // eslint-disable-next-line react/forbid-dom-props
        style={{ ["--hue-color" as string]: hueColor } as React.CSSProperties}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onKeyDown={handlePaletteKeyDown}
      >
        <span
          className="ctf-sv-cursor"
          // eslint-disable-next-line react/forbid-dom-props
          style={{ left: `${hsv.s}%`, top: `${100 - hsv.v}%`, ["--cursor-color" as string]: color } as React.CSSProperties}
        />
      </div>
      <div className="ctf-hue-row">
        <span className="ctf-hue-label">Hue</span>
        <input
          type="range"
          className="ctf-hue-slider"
          min="0"
          max="359"
          value={hsv.h}
          aria-label={`${label} hue`}
          onChange={(event) => onChange(rgbToHex(hsvToRgb({ ...hsv, h: Number(event.target.value) })))}
        />
        <span className="ctf-hue-value">{hsv.h}&deg;</span>
      </div>
    </div>
  );
};

const CustomTimeframeModal: React.FC<CustomTimeframeModalProps> = ({
  isOpen,
  onClose,
  theme,
  onTimeframesChanged,
}) => {
  const dispatch = useDispatch();
  const alertSoundEnabled = useSelector((state: any) => state.alerts?.alertSoundEnabled ?? true);
  const [activeTab, setActiveTab] = useState<ActiveTab>("timeframes");
  const [customTfs, setCustomTfs] = useState<CustomTimeframe[]>([]);
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("m");
  const [editKey, setEditKey] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [backfillDays, setBackfillDays] = useState<BackfillDays>(loadBackfillDays);
  const [eodDuration, setEodDuration] = useState<EodDuration>(loadEodDuration);
  const [selectedBgId, setSelectedBgId] = useState<string>(loadChartBgId);
  const [appliedUpColor, setAppliedUpColor] = useState<string>(loadChartSettings().upColor);
  const [appliedDownColor, setAppliedDownColor] = useState<string>(loadChartSettings().downColor);
  const [upCandleColor, setUpCandleColor] = useState<string>(loadChartSettings().upColor);
  const [downCandleColor, setDownCandleColor] = useState<string>(loadChartSettings().downColor);
  const [upColorInput, setUpColorInput] = useState<string>(loadChartSettings().upColor);
  const [downColorInput, setDownColorInput] = useState<string>(loadChartSettings().downColor);
  const [activeColorEditor, setActiveColorEditor] = useState<CandleColorField | null>(null);
  const [styleError, setStyleError] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Load on open
  useEffect(() => {
    if (isOpen) {
      setCustomTfs(loadCustomTimeframes());
      setValue("");
      setUnit("m");
      setEditKey(null);
      setError("");
      setBackfillDays(loadBackfillDays());
      setEodDuration(loadEodDuration());
      setSelectedBgId(loadChartBgId());
      const saved = loadChartSettings();
      setAppliedUpColor(saved.upColor);
      setAppliedDownColor(saved.downColor);
      setUpCandleColor(saved.upColor);
      setDownCandleColor(saved.downColor);
      setUpColorInput(saved.upColor);
      setDownColorInput(saved.downColor);
      setActiveColorEditor(null);
      setStyleError("");
      setActiveTab("timeframes");
    }
  }, [isOpen]);

  // Focus input when modal opens or edit starts
  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen, editKey]);

  const getMinutes = useCallback((val: number, u: string) => {
    const opt = UNIT_OPTIONS.find(o => o.value === u);
    return val * (opt?.multiplier ?? 1);
  }, []);

  const makeLabel = useCallback((val: number, u: string) => {
    if (u === "Mo") return `${val}Mo`;
    return `${val}${u}`;
  }, []);

  const makeKey = useCallback((val: number, u: string) => {
    // Day-based units use their suffix directly: "2W", "3Mo", "2Y"
    if (u === "W" || u === "Mo" || u === "Y") return `${val}${u}`;
    // Hours: "2H", "3H"
    if (u === "H") return `${val}H`;
    // Minutes: total minutes as string
    return String(val * (UNIT_OPTIONS.find(o => o.value === u)?.multiplier ?? 1));
  }, []);

  const handleAdd = useCallback(() => {
    const num = parseInt(value, 10);
    if (!num || num <= 0) {
      setError("Enter a positive number");
      return;
    }

    // Validation limits per unit
    if (unit === "m" || unit === "H") {
      const minutes = getMinutes(num, unit);
      if (minutes > 1440) {
        setError("Maximum is 1440 minutes (1 day)");
        return;
      }
    } else if (unit === "W" && num > 52) {
      setError("Maximum is 52 weeks");
      return;
    } else if (unit === "Mo" && num > 12) {
      setError("Maximum is 12 months");
      return;
    } else if (unit === "Y" && num > 10) {
      setError("Maximum is 10 years");
      return;
    }

    const minutes = getMinutes(num, unit);
    const key = makeKey(num, unit);
    const label = makeLabel(num, unit);

    // Check for duplicates in built-in
    if (BUILT_IN_TIMEFRAMES.includes(key) || TIMEFRAME_CONFIGS[key]) {
      // Also check minutes collision with built-in
      const existingBuiltIn = BUILT_IN_TIMEFRAMES.find(
        bi => TIMEFRAME_CONFIGS[bi]?.minutes === minutes
      );
      if (existingBuiltIn) {
        setError(`Already exists as built-in "${TIMEFRAME_CONFIGS[existingBuiltIn].label}"`);
        return;
      }
    }

    // Check for duplicates in custom (by minutes, excluding currently editing)
    const dupCustom = customTfs.find(
      ct => ct.minutes === minutes && ct.key !== editKey
    );
    if (dupCustom) {
      setError(`Already exists as custom "${dupCustom.label}"`);
      return;
    }

    let updated: CustomTimeframe[];
    if (editKey) {
      // Editing: replace
      unregisterCustomTimeframe(editKey);
      updated = customTfs.map(ct =>
        ct.key === editKey ? { key, minutes, label } : ct
      );
    } else {
      // Adding
      updated = [...customTfs, { key, minutes, label }];
    }

    // Sort by minutes
    updated.sort((a, b) => a.minutes - b.minutes);
    setCustomTfs(updated);
    syncSaveCustomTimeframes(updated);

    // Register in runtime config
    registerCustomTimeframe(key, { minutes, label });

    setValue("");
    setUnit("m");
    setEditKey(null);
    setError("");
    onTimeframesChanged?.();
  }, [value, unit, customTfs, editKey, getMinutes, makeKey, makeLabel, onTimeframesChanged]);

  const handleEdit = useCallback((ct: CustomTimeframe) => {
    // Parse unit from key
    if (ct.key.endsWith("Mo")) {
      setValue(ct.key.replace("Mo", ""));
      setUnit("Mo");
    } else if (ct.key.endsWith("Y")) {
      setValue(ct.key.replace("Y", ""));
      setUnit("Y");
    } else if (ct.key.endsWith("W")) {
      setValue(ct.key.replace("W", ""));
      setUnit("W");
    } else if (ct.key.endsWith("H")) {
      setValue(ct.key.replace("H", ""));
      setUnit("H");
    } else {
      setValue(String(ct.minutes));
      setUnit("m");
    }
    setEditKey(ct.key);
    setError("");
  }, []);

  const handleDelete = useCallback((key: string) => {
    unregisterCustomTimeframe(key);
    const updated = customTfs.filter(ct => ct.key !== key);
    setCustomTfs(updated);
    syncSaveCustomTimeframes(updated);
    if (editKey === key) {
      setEditKey(null);
      setValue("");
      setUnit("m");
    }
    setError("");
    onTimeframesChanged?.();
  }, [customTfs, editKey, onTimeframesChanged]);

  const handleCancel = useCallback(() => {
    setEditKey(null);
    setValue("");
    setUnit("m");
    setError("");
  }, []);

  const handleBackfillSelect = useCallback((days: BackfillDays) => {
    setBackfillDays(days);
    saveBackfillDays(days);
  }, []);

  const handleEodDurationSelect = useCallback((days: EodDuration) => {
    setEodDuration(days);
    saveEodDuration(days);
    // Notify chart panels to re-fetch EOD data with the new duration
    window.dispatchEvent(new Event("eod-duration-changed"));
  }, []);

  const handleBgSelect = useCallback((preset: ChartBgPreset) => {
    setSelectedBgId(preset.id);
    saveChartBg(preset.id);
    window.dispatchEvent(new Event("chart-bg-changed"));
  }, []);

  const persistCandleColors = useCallback((nextUp: string, nextDown: string) => {
    const settings = {
      ...loadChartSettings(),
      upColor: nextUp,
      downColor: nextDown,
    };

    void syncSaveChartSettings(settings);
    window.dispatchEvent(new CustomEvent("chart-settings-changed", { detail: settings }));
  }, []);

  const updateDraftCandleColor = useCallback((field: CandleColorField, value: string) => {
    const normalized = normalizeHexColor(value);
    if (!normalized) {
      setStyleError("Please enter a valid hex color like #16a34a or #dc2626.");
      return;
    }

    setStyleError("");

    if (field === "up") {
      setUpCandleColor(normalized);
      setUpColorInput(normalized);
    } else {
      setDownCandleColor(normalized);
      setDownColorInput(normalized);
    }
  }, []);

  const handleColorInputBlur = useCallback((field: CandleColorField) => {
    const raw = field === "up" ? upColorInput : downColorInput;
    const normalized = normalizeHexColor(raw);
    if (!normalized) {
      setStyleError("Please enter a valid hex color like #16a34a or #dc2626.");
      return;
    }
    updateDraftCandleColor(field, normalized);
  }, [downColorInput, upColorInput, updateDraftCandleColor]);

  const updateRgbChannel = useCallback((field: CandleColorField, channel: RgbChannel, value: number) => {
    const currentColor = field === "up" ? upCandleColor : downCandleColor;
    const nextRgb = {
      ...hexToRgb(currentColor),
      [channel]: Math.max(0, Math.min(255, Math.round(value))),
    };
    updateDraftCandleColor(field, rgbToHex(nextRgb));
  }, [downCandleColor, upCandleColor, updateDraftCandleColor]);

  const handleStyleReset = useCallback(() => {
    setStyleError("");
    setUpCandleColor(DEFAULT_CANDLE_COLORS.upColor);
    setDownCandleColor(DEFAULT_CANDLE_COLORS.downColor);
    setUpColorInput(DEFAULT_CANDLE_COLORS.upColor);
    setDownColorInput(DEFAULT_CANDLE_COLORS.downColor);
  }, []);

  const handleApplyCandleColors = useCallback(() => {
    const normalizedUp = normalizeHexColor(upColorInput);
    const normalizedDown = normalizeHexColor(downColorInput);

    if (!normalizedUp || !normalizedDown) {
      setStyleError("Please enter valid hex colors before applying.");
      return;
    }

    setStyleError("");
    setUpCandleColor(normalizedUp);
    setDownCandleColor(normalizedDown);
    setUpColorInput(normalizedUp);
    setDownColorInput(normalizedDown);
    setAppliedUpColor(normalizedUp);
    setAppliedDownColor(normalizedDown);
    persistCandleColors(normalizedUp, normalizedDown);
  }, [downColorInput, persistCandleColors, upColorInput]);

  const handleAlertSoundChange = useCallback((enabled: boolean) => {
    dispatch(setAlertSoundEnabled(enabled));
  }, [dispatch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleAdd();
    if (e.key === "Escape") {
      if (editKey) handleCancel();
      else onClose();
    }
  }, [handleAdd, editKey, handleCancel, onClose]);

  if (!isOpen) return null;

  const portalTarget = document.getElementById("marketview") || document.body;

  const builtInList = BUILT_IN_TIMEFRAMES.map(key => ({
    key,
    ...TIMEFRAME_CONFIGS[key],
  }));

  const hasDraftChanges = upCandleColor !== appliedUpColor || downCandleColor !== appliedDownColor;
  const upRgb = hexToRgb(upCandleColor);
  const downRgb = hexToRgb(downCandleColor);

  const modalContent = (
    <div
      className={`ctf-modal-overlay ${theme}`}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ background: "none", backdropFilter: "none" }}
    >
      <div className="ctf-modal-container">
        <div className="ctf-modal-header">
          <h3 className="ctf-modal-title">Preferences</h3>
          <button className="ctf-modal-close" onClick={onClose} title="Close">
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="4" y1="4" x2="16" y2="16" />
              <line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          </button>
        </div>

        {/* ── Tabs ── */}
        <div className="ctf-tabs">
          <button
            className={`ctf-tab ${activeTab === "timeframes" ? "ctf-tab-active" : ""}`}
            onClick={() => setActiveTab("timeframes")}
          >
            Timeframes
          </button>
          <button
            className={`ctf-tab ${activeTab === "backfill" ? "ctf-tab-active" : ""}`}
            onClick={() => setActiveTab("backfill")}
          >
            Backfill History
          </button>
          <button
            className={`ctf-tab ${activeTab === "background" ? "ctf-tab-active" : ""}`}
            onClick={() => setActiveTab("background")}
          >
            Background
          </button>
          <button
            className={`ctf-tab ${activeTab === "style" ? "ctf-tab-active" : ""}`}
            onClick={() => setActiveTab("style")}
          >
            Candle Color
          </button>
          <button
            className={`ctf-tab ${activeTab === "alerts" ? "ctf-tab-active" : ""}`}
            onClick={() => setActiveTab("alerts")}
          >
            Alerts
          </button>
        </div>

        <div className="ctf-modal-body">
          {activeTab === "timeframes" && (
            <>
              {/* Add/Edit form */}
              <div className="ctf-form">
                <div className="ctf-form-row">
                  <input
                    ref={inputRef}
                    type="number"
                    min="1"
                    max="9999"
                    className="ctf-input"
                    placeholder="Value"
                    value={value}
                    onChange={e => { setValue(e.target.value); setError(""); }}
                    onKeyDown={handleKeyDown}
                  />
                  <select
                    className="ctf-select"
                    value={unit}
                    title="Time unit"
                    onChange={e => { setUnit(e.target.value); setError(""); }}
                  >
                    {UNIT_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button className="ctf-btn ctf-btn-primary" onClick={handleAdd}>
                    {editKey ? "Update" : "Add"}
                  </button>
                  {editKey && (
                    <button className="ctf-btn ctf-btn-secondary" onClick={handleCancel}>
                      Cancel
                    </button>
                  )}
                </div>
                {error && <div className="ctf-error">{error}</div>}
              </div>

              {/* Built-in timeframes (read-only) */}
              <div className="ctf-section">
                <div className="ctf-section-label">Built-in</div>
                <div className="ctf-list">
                  {builtInList.map(tf => (
                    <div key={tf.key} className="ctf-item ctf-item-builtin">
                      <span className="ctf-item-label">{tf.label}</span>
                      <span className="ctf-item-detail">
                        {tf.minutes < 1440 ? `${tf.minutes} min`
                          : tf.minutes === 1440 ? "1 day"
                          : tf.minutes === 10080 ? "1 week"
                          : tf.minutes === 43800 ? "1 month"
                          : tf.minutes === 262800 ? "6 months"
                          : tf.minutes === 525600 ? "1 year"
                          : `${Math.round(tf.minutes / 1440)} days`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom timeframes */}
              <div className="ctf-section">
                <div className="ctf-section-label">Custom</div>
                <div className="ctf-list">
                  {customTfs.length === 0 && (
                    <div className="ctf-empty">No custom timeframes added yet.</div>
                  )}
                  {customTfs.map(ct => (
                    <div key={ct.key} className={`ctf-item ${editKey === ct.key ? "ctf-item-editing" : ""}`}>
                      <span className="ctf-item-label">{ct.label}</span>
                      <span className="ctf-item-detail">
                        {ct.minutes < 1440 ? `${ct.minutes} min`
                          : ct.key.endsWith("W") ? `${ct.key.replace("W", "")} week(s)`
                          : ct.key.endsWith("Mo") ? `${ct.key.replace("Mo", "")} month(s)`
                          : ct.key.endsWith("Y") ? `${ct.key.replace("Y", "")} year(s)`
                          : `${ct.minutes} min`}
                      </span>
                      <div className="ctf-item-actions">
                        <button className="ctf-item-btn ctf-item-btn-edit" onClick={() => handleEdit(ct)} title="Edit">
                          <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11.5 3.5l5 5L6 19H1v-5L11.5 3.5z" />
                          </svg>
                        </button>
                        <button className="ctf-item-btn ctf-item-btn-delete" onClick={() => handleDelete(ct.key)} title="Delete">
                          <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h14M8 6V4a1 1 0 011-1h2a1 1 0 011 1v2m2 0v11a2 2 0 01-2 2H8a2 2 0 01-2-2V6h8z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {activeTab === "backfill" && (
            <div className="ctf-backfill">
              <p className="ctf-backfill-desc">
                Choose how many days of 1-minute historical bars to fetch when
                a symbol is opened. A shorter period loads faster; a longer
                period provides more chart history.
              </p>
              <div className="ctf-backfill-options">
                {(BACKFILL_DAYS_OPTIONS as readonly BackfillDays[]).map(days => (
                  <button
                    key={days}
                    className={`ctf-backfill-option ${
                      backfillDays === days ? "ctf-backfill-option-selected" : ""
                    }`}
                    onClick={() => handleBackfillSelect(days)}
                  >
                    {BACKFILL_LABELS[days]}
                    {backfillDays === days && (
                      <svg className="ctf-backfill-check" viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2,6 5,9 10,3" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
              <p className="ctf-backfill-desc ctf-eod-desc">
                Choose how much daily history to load for 1D and above
                timeframes. Longer periods take more time to load.
              </p>
              <div className="ctf-backfill-options">
                {(EOD_DURATION_OPTIONS as readonly EodDuration[]).map(days => (
                  <button
                    key={days}
                    className={`ctf-backfill-option ${
                      eodDuration === days ? "ctf-backfill-option-selected" : ""
                    }`}
                    onClick={() => handleEodDurationSelect(days)}
                  >
                    {EOD_DURATION_LABELS[days]}
                    {eodDuration === days && (
                      <svg className="ctf-backfill-check" viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2,6 5,9 10,3" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>

            </div>
          )}

          {activeTab === "background" && (
            <div className="ctf-bg-tab">
              <p className="ctf-backfill-desc">
                Choose a background color for the chart area. The foreground
                colors (text, grid, scales) will automatically adapt for
                maximum readability.
              </p>

              {/* ── Dark category ── */}
              <div className="ctf-bg-category">
                <div className="ctf-section-label">Dark Backgrounds</div>
                <div className="ctf-bg-grid">
                  {CHART_BG_PRESETS.filter(p => p.category === "dark").map(preset => (
                    <button
                      key={preset.id}
                      className={`ctf-bg-swatch ${selectedBgId === preset.id ? "ctf-bg-swatch-active" : ""}`}
                      onClick={() => handleBgSelect(preset)}
                      title={preset.label}
                      data-bg={preset.bg}
                    >
                      <span
                        className="ctf-bg-swatch-color"
                        // eslint-disable-next-line react/forbid-dom-props
                        style={{ ["--swatch-bg" as string]: preset.bg } as React.CSSProperties}
                      >
                        {selectedBgId === preset.id && (
                          <svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="2,7 6,11 12,3" />
                          </svg>
                        )}
                      </span>
                      <span className="ctf-bg-swatch-label">{preset.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Light category ── */}
              <div className="ctf-bg-category">
                <div className="ctf-section-label">Light Backgrounds</div>
                <div className="ctf-bg-grid">
                  {CHART_BG_PRESETS.filter(p => p.category === "light").map(preset => (
                    <button
                      key={preset.id}
                      className={`ctf-bg-swatch ${selectedBgId === preset.id ? "ctf-bg-swatch-active" : ""}`}
                      onClick={() => handleBgSelect(preset)}
                      title={preset.label}
                      data-bg={preset.bg}
                    >
                      <span
                        className="ctf-bg-swatch-color ctf-bg-swatch-light"
                        // eslint-disable-next-line react/forbid-dom-props
                        style={{ ["--swatch-bg" as string]: preset.bg } as React.CSSProperties}
                      >
                        {selectedBgId === preset.id && (
                          <svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="#1a1a1a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="2,7 6,11 12,3" />
                          </svg>
                        )}
                      </span>
                      <span className="ctf-bg-swatch-label">{preset.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "style" && (
            <div className="ctf-style-tab">
              <p className="ctf-backfill-desc">
                Configure default candle colors used in candlestick and bar charts. Bull candles use
                the Up color and bear candles use the Down color.
              </p>

              <div className="ctf-style-preview-card">
                <div className="ctf-style-preview-head">
                  <span className="ctf-style-preview-title">Live Preview</span>
                  <span className="ctf-style-preview-subtitle">Preview only. Click Apply to save for charts.</span>
                </div>
                <div className="ctf-style-preview-chart">
                  <div className="ctf-style-mini-candle ctf-style-mini-candle-bull">
                    <span
                      className="ctf-style-mini-wick"
                      // eslint-disable-next-line react/forbid-dom-props
                      style={{ ["--candle-color" as string]: upCandleColor } as React.CSSProperties}
                    />
                    <span
                      className="ctf-style-mini-body"
                      // eslint-disable-next-line react/forbid-dom-props
                      style={{ ["--candle-color" as string]: upCandleColor } as React.CSSProperties}
                    />
                  </div>
                  <div className="ctf-style-mini-candle ctf-style-mini-candle-bear">
                    <span
                      className="ctf-style-mini-wick"
                      // eslint-disable-next-line react/forbid-dom-props
                      style={{ ["--candle-color" as string]: downCandleColor } as React.CSSProperties}
                    />
                    <span
                      className="ctf-style-mini-body"
                      // eslint-disable-next-line react/forbid-dom-props
                      style={{ ["--candle-color" as string]: downCandleColor } as React.CSSProperties}
                    />
                  </div>
                </div>
              </div>

              <div className="ctf-style-card">
                <div className="ctf-style-row">
                  <div className="ctf-style-labels">
                    <div className="ctf-style-title">Bull Candle (Up)</div>
                    <div className="ctf-style-subtitle">Applied when close is greater than or equal to open.</div>
                  </div>
                  <div className="ctf-style-controls">
                    <button
                      type="button"
                      className={`ctf-style-color-trigger ${activeColorEditor === "up" ? "ctf-style-color-trigger-active" : ""}`}
                      title="Pick bull candle color"
                      aria-label="Open bull candle RGB editor"
                      aria-expanded={activeColorEditor === "up"}
                      onClick={() => setActiveColorEditor(current => current === "up" ? null : "up")}
                    >
                      <span
                        className="ctf-style-color-trigger-swatch"
                        // eslint-disable-next-line react/forbid-dom-props
                        style={{ ["--picker-color" as string]: upCandleColor } as React.CSSProperties}
                      />
                      <span className="ctf-style-color-trigger-label">RGB</span>
                    </button>
                    <input
                      type="text"
                      className="ctf-style-hex-input"
                      value={upColorInput}
                      maxLength={7}
                      placeholder="#16a34a"
                      onChange={(e) => { setUpColorInput(e.target.value); setStyleError(""); }}
                      onBlur={() => handleColorInputBlur("up")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleColorInputBlur("up");
                        }
                      }}
                    />
                  </div>
                </div>
                {activeColorEditor === "up" && (
                  <div className="ctf-rgb-editor" aria-label="Bull candle RGB controls">
                    <AdvancedColorSurface
                      color={upCandleColor}
                      label="Bull candle"
                      onChange={(color) => updateDraftCandleColor("up", color)}
                    />
                    {(["r", "g", "b"] as const).map((channel) => (
                      <label key={channel} className={`ctf-rgb-row ctf-rgb-row-${channel}`}>
                        <span className="ctf-rgb-channel">{channel.toUpperCase()}</span>
                        <input
                          type="range"
                          className="ctf-rgb-slider"
                          min="0"
                          max="255"
                          value={upRgb[channel]}
                          aria-label={`Bull candle ${channel.toUpperCase()} value`}
                          onChange={(e) => updateRgbChannel("up", channel, Number(e.target.value))}
                        />
                        <input
                          type="number"
                          className="ctf-rgb-number"
                          min="0"
                          max="255"
                          value={upRgb[channel]}
                          aria-label={`Bull candle ${channel.toUpperCase()} numeric value`}
                          onChange={(e) => updateRgbChannel("up", channel, Number(e.target.value))}
                        />
                      </label>
                    ))}
                  </div>
                )}
                <div className="ctf-style-presets">
                  {BULL_COLOR_PRESETS.map((color) => (
                    <button
                      key={color}
                      className={`ctf-style-swatch ${upCandleColor === color ? "ctf-style-swatch-active" : ""}`}
                      onClick={() => updateDraftCandleColor("up", color)}
                      title={color}
                      // eslint-disable-next-line react/forbid-dom-props
                      style={{ ["--swatch-color" as string]: color } as React.CSSProperties}
                    />
                  ))}
                </div>
              </div>

              <div className="ctf-style-card">
                <div className="ctf-style-row">
                  <div className="ctf-style-labels">
                    <div className="ctf-style-title">Bear Candle (Down)</div>
                    <div className="ctf-style-subtitle">Applied when close is lower than open.</div>
                  </div>
                  <div className="ctf-style-controls">
                    <button
                      type="button"
                      className={`ctf-style-color-trigger ${activeColorEditor === "down" ? "ctf-style-color-trigger-active" : ""}`}
                      title="Pick bear candle color"
                      aria-label="Open bear candle RGB editor"
                      aria-expanded={activeColorEditor === "down"}
                      onClick={() => setActiveColorEditor(current => current === "down" ? null : "down")}
                    >
                      <span
                        className="ctf-style-color-trigger-swatch"
                        // eslint-disable-next-line react/forbid-dom-props
                        style={{ ["--picker-color" as string]: downCandleColor } as React.CSSProperties}
                      />
                      <span className="ctf-style-color-trigger-label">RGB</span>
                    </button>
                    <input
                      type="text"
                      className="ctf-style-hex-input"
                      value={downColorInput}
                      maxLength={7}
                      placeholder="#dc2626"
                      onChange={(e) => { setDownColorInput(e.target.value); setStyleError(""); }}
                      onBlur={() => handleColorInputBlur("down")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleColorInputBlur("down");
                        }
                      }}
                    />
                  </div>
                </div>
                {activeColorEditor === "down" && (
                  <div className="ctf-rgb-editor" aria-label="Bear candle RGB controls">
                    <AdvancedColorSurface
                      color={downCandleColor}
                      label="Bear candle"
                      onChange={(color) => updateDraftCandleColor("down", color)}
                    />
                    {(["r", "g", "b"] as const).map((channel) => (
                      <label key={channel} className={`ctf-rgb-row ctf-rgb-row-${channel}`}>
                        <span className="ctf-rgb-channel">{channel.toUpperCase()}</span>
                        <input
                          type="range"
                          className="ctf-rgb-slider"
                          min="0"
                          max="255"
                          value={downRgb[channel]}
                          aria-label={`Bear candle ${channel.toUpperCase()} value`}
                          onChange={(e) => updateRgbChannel("down", channel, Number(e.target.value))}
                        />
                        <input
                          type="number"
                          className="ctf-rgb-number"
                          min="0"
                          max="255"
                          value={downRgb[channel]}
                          aria-label={`Bear candle ${channel.toUpperCase()} numeric value`}
                          onChange={(e) => updateRgbChannel("down", channel, Number(e.target.value))}
                        />
                      </label>
                    ))}
                  </div>
                )}
                <div className="ctf-style-presets">
                  {BEAR_COLOR_PRESETS.map((color) => (
                    <button
                      key={color}
                      className={`ctf-style-swatch ${downCandleColor === color ? "ctf-style-swatch-active" : ""}`}
                      onClick={() => updateDraftCandleColor("down", color)}
                      title={color}
                      // eslint-disable-next-line react/forbid-dom-props
                      style={{ ["--swatch-color" as string]: color } as React.CSSProperties}
                    />
                  ))}
                </div>
              </div>

              <div className="ctf-style-actions">
                <button className="ctf-btn ctf-btn-primary" onClick={handleApplyCandleColors} disabled={!hasDraftChanges}>
                  Apply Candle Colors
                </button>
                <button className="ctf-btn ctf-btn-secondary" onClick={handleStyleReset}>
                  Reset Draft
                </button>
              </div>

              {styleError && <div className="ctf-error ctf-style-error">{styleError}</div>}
            </div>
          )}

          {activeTab === "alerts" && (
            <div className="ctf-alerts-tab">
              <p className="ctf-backfill-desc">
                Control price-alert sound notification. When enabled, an alert beep
                will play whenever a price alert is triggered.
              </p>

              <div className="ctf-alerts-row">
                <div className="ctf-alerts-copy">
                  <div className="ctf-alerts-title">Alert Sound</div>
                  <div className="ctf-alerts-subtitle">
                    {alertSoundEnabled ? "Enabled" : "Disabled"}
                  </div>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={alertSoundEnabled}
                  aria-label="Toggle alert sound"
                  className={`ctf-switch ${alertSoundEnabled ? "ctf-switch-on" : ""}`}
                  onClick={() => handleAlertSoundChange(!alertSoundEnabled)}
                >
                  <span className="ctf-switch-thumb" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, portalTarget);
};

export default CustomTimeframeModal;
